import React, {type ReactNode, useMemo} from 'react';
import {Resizable, type ResizeHandle} from 'react-resizable';
import {twMerge} from 'tailwind-merge';

import {useIsMobile} from '../../../util/panelbankFlow';
import {DEFAULT_PANEL_SIZE} from '../../../util/panelbankTypes';
import {
  getMaxSectionWidth,
  getPanelTransform,
} from '../../PanelBank/sectionLayout/resizeUtils';
import {useWorkspaceSectionLayoutConfigs} from '../../PanelBank/sectionLayout/WorkspaceSectionLayoutConfigs';
import {useResizableWorkspacePanel} from './useResizableWorkspacePanel';
import {type WorkspacePanelResizePreview} from './WorkspacePanelResizePreview';

const RESIZEABLE_PANEL_HANDLE_DIRECTION: ResizeHandle[] = ['se'];
const RESIZEABLE_PANEL_HANDLE_SIZE: [width: number, height: number] = [20, 20];
const RESIZABLE_PANEL_MIN_CONSTRAINTS: [width: number, height: number] = [
  DEFAULT_PANEL_SIZE,
  DEFAULT_PANEL_SIZE,
];

type ResizableWorkspacePanelProps = {
  children: ReactNode;
  index: number;
  /**
   * This value represents how many panels a user can visually see in the section.
   * Filters, search, etc can and will affect this value.
   */
  numVisiblePanels: number;
  panelResizePreview: WorkspacePanelResizePreview | null;
  setPanelResizePreview: React.Dispatch<
    React.SetStateAction<WorkspacePanelResizePreview | null>
  >;
};

export const ResizableWorkspacePanel = ({
  children,
  index,
  numVisiblePanels,
  panelResizePreview,
  setPanelResizePreview,
}: ResizableWorkspacePanelProps) => {
  const isMobileLayout = useIsMobile();
  const {columnsPerPage, panelHeight, panelWidth} =
    useWorkspaceSectionLayoutConfigs();

  const {handleOnResizeStart, handleOnResize, handleResizeStop} =
    useResizableWorkspacePanel({
      index,
      numVisiblePanels,
      setPanelResizePreview,
    });

  const transform = useMemo(() => {
    return getPanelTransform({
      index,
      columnsPerPage,
      panelWidth,
      panelHeight,
    });
  }, [index, columnsPerPage, panelWidth, panelHeight]);

  const maxConstraints: [width: number, height: number] = useMemo(() => {
    return [
      // A panel can only be as wide as the panel bank width minus the margins
      getMaxSectionWidth({columnsPerPage, panelWidth}),
      // This is arbitrarily chosen. We'll allow a panel height to be doubled
      // and prevent users from ending up in a bad state.
      panelHeight * 2,
    ];
  }, [columnsPerPage, panelWidth, panelHeight]);

  return (
    // We need to pass the transform as an inline style because tw doesn't support variables.
    // The tw group property is used to decide if the resizable handle should be displayed when cursor hovers over the panel.
    <div
      // HAX: there's an existing hack in LinePlotCrosshair.tsx so the tooltip doesn't get cut off by the panel boundaries.
      // As a result, we need to apply a z-index of 5 to the panel so that the line plot crosshair flag appears over other panels.
      // .line-plot-panel-crosshair-hover at the workspace-panel level controls which crosshair flag content to display.
      // Ideally, we should move off of this because it's very easy to break.
      className="group absolute transition-none hover:z-[5] "
      data-testid="resizable-workspace-panel"
      style={{transform}}>
      {/*
        We are intentionally NOT using ReziableBox (unlike section resizing) because the use case is more complex here.
        Instead of resizing the panel directly, we want to render a resize preview and have it overlay the other panels.
      */}
      <Resizable
        className="h-full w-full"
        // We only allow 1 column (aka 1 panel per row) on mobile.
        // so we only allow vertical resizing on mobile.
        axis={isMobileLayout ? 'y' : 'both'}
        height={panelResizePreview?.height ?? panelHeight}
        width={panelResizePreview?.width ?? panelWidth}
        resizeHandles={RESIZEABLE_PANEL_HANDLE_DIRECTION}
        handleSize={RESIZEABLE_PANEL_HANDLE_SIZE}
        handle={
          <div
            className={twMerge(
              'react-resizable-handle react-resizable-handle-se',
              'opacity-0 group-hover:opacity-100'
            )}
            data-testid="resizable-workspace-panel-handle"
          />
        }
        // A section cannot be smaller than the current dimensions of a panel.
        // There is always a minimum at least one row in a section if a panel is present.
        minConstraints={RESIZABLE_PANEL_MIN_CONSTRAINTS}
        maxConstraints={maxConstraints}
        onResizeStart={handleOnResizeStart}
        onResize={handleOnResize}
        onResizeStop={handleResizeStop}>
        {/*
          In order for the resize handle to properly appear in the lower right corner,
          the children needs to be wrapped in a div. This helps create a block-level element.
        */}
        <div>{children}</div>
      </Resizable>
    </div>
  );
};
